import { useMemo, useState } from "react";

import { Bounds } from "@nens/deck-gl";
import { Raster } from "@nens/lizard/api-v4";

import { useWmsGetColorRangeQuery } from "@/queries/query-wms-color-range";
import { RasterFileDescriptor, ScenarioFileDescriptor } from "@/types/descriptor-types";

type ColorMode =
  | { type: "bounds"; bounds: Bounds }
  | { type: "range"; range: [number, number] }
  | undefined;

export function useRasterWithColorRange(
  descriptor: RasterFileDescriptor | ScenarioFileDescriptor | undefined,
  raster: Raster | undefined
) {
  const [colorMode, setColorMode] = useState<ColorMode>(undefined);

  const { data: fetchedColorRange } = useWmsGetColorRangeQuery(
    descriptor,
    raster?.wms_info.layer,
    colorMode?.type === "bounds" ? colorMode.bounds : undefined
  );

  const colorRange = colorMode?.type === "range" ? colorMode.range : fetchedColorRange;

  const boundsRaster = useMemo(() => {
    if (!raster || !colorRange) return raster;

    const [min, max] = colorRange;
    const [styleBase] = raster.options.styles.split(":");

    return {
      ...raster,
      options: {
        ...raster.options,
        styles: `${styleBase}:${min}:${max}`,
      },
    };
  }, [raster, colorRange]);

  return {
    raster: boundsRaster,
    setDefaultColor: () => setColorMode(undefined),
    setColorBounds: (bounds: Bounds) => setColorMode({ type: "bounds", bounds }),
    setColorRange: (range: [number, number]) => setColorMode({ type: "range", range }),
  };
}
